import { useEffect, useState } from "react";

import {
  acceptWorkspaceInvitation,
  AccountRequestError,
  previewWorkspaceInvitation,
  type WorkspaceInvitationPreview,
} from "../../lib/account-client";
import { startGoogleSignIn } from "../../lib/auth-client";
import type { ManagementSessionState } from "../../lib/manage-session";

type PreviewState =
  | { kind: "loading" }
  | { kind: "ready"; invitation: WorkspaceInvitationPreview }
  | { kind: "error"; heading: string; message: string };

type AcceptanceState =
  | { kind: "idle" }
  | { kind: "working" }
  | { kind: "accepted"; workspaceId: string }
  | { kind: "error"; heading: string; message: string };

export function InvitationAcceptPage({
  token,
  session,
  preview = previewWorkspaceInvitation,
  accept = acceptWorkspaceInvitation,
  signIn = async (returnPath) => startGoogleSignIn({ returnPath }),
}: {
  token: string | null;
  session: ManagementSessionState;
  preview?: typeof previewWorkspaceInvitation;
  accept?: typeof acceptWorkspaceInvitation;
  signIn?: (returnPath: string) => Promise<void>;
}) {
  const [previewState, setPreviewState] = useState<PreviewState>(token
    ? { kind: "loading" }
    : {
        kind: "error",
        heading: "Invitation link incomplete",
        message: "Ask the workspace owner to send the invitation again.",
      });
  const [acceptance, setAcceptance] = useState<AcceptanceState>({ kind: "idle" });
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let active = true;
    setPreviewState({ kind: "loading" });
    void preview(token).then((invitation) => {
      if (active) setPreviewState({ kind: "ready", invitation });
    }).catch((cause: unknown) => {
      if (active) setPreviewState(invitationPreviewFailure(cause));
    });
    return () => { active = false; };
  }, [preview, token]);

  if (previewState.kind === "loading") {
    return <InvitationStatus heading="Opening your invitation" message="Checking the workspace details…" busy />;
  }
  if (previewState.kind === "error") {
    return <InvitationStatus heading={previewState.heading} message={previewState.message} />;
  }

  const invitation = previewState.invitation;
  if (acceptance.kind === "accepted") {
    return (
      <InvitationStatus
        heading="You’re in"
        message={`You now have access to ${invitation.workspaceName}.`}
        workspaceId={acceptance.workspaceId}
      />
    );
  }

  const terminal = terminalInvitationCopy(invitation.status);
  const actionError = acceptance.kind === "error" ? acceptance : null;

  const join = () => {
    if (!token || acceptance.kind === "working") return;
    setAcceptance({ kind: "working" });
    void accept(token).then((result) => {
      setAcceptance({ kind: "accepted", workspaceId: result.workspaceId });
    }).catch((cause: unknown) => {
      setAcceptance(invitationFailureState(cause));
    });
  };

  return (
    <main className="invitation-main">
      <section className="invitation-card" aria-labelledby="invitation-heading">
        <div className="invitation-story">
          <div className="invitation-door" aria-hidden="true"><span /></div>
          <p className="eyebrow">A shared Doorway</p>
          <h2>Small apps become useful when the right people can open them.</h2>
          <p>Doorway turns finished app folders into links your team can use without learning hosting.</p>
          <ul>
            <li>Open shared apps from one workspace</li>
            <li>Keep internal tools behind roles and passwords</li>
            <li>Publish updates without Git or deployment setup</li>
          </ul>
        </div>

        <div className="invitation-details">
          <p className="eyebrow">Doorway invitation</p>
          <h1 id="invitation-heading">Join {invitation.workspaceName}</h1>
          <p className="invitation-from">{invitation.inviterName} invited you</p>

          <dl className="invitation-facts">
            <div><dt>Workspace</dt><dd>{invitation.workspaceName}</dd></div>
            <div><dt>Your role</dt><dd>{roleLabel(invitation.role)} access</dd></div>
            <div><dt>Google account</dt><dd>{invitation.invitedEmail}</dd></div>
          </dl>

          {terminal ? (
            <div className="invitation-notice" role="alert">
              <h2>{terminal.heading}</h2>
              <p>{terminal.message}</p>
              {invitation.status === "accepted" && (
                <a className="primary-button" href={`/manage/workspaces/${encodeURIComponent(invitation.workspaceId)}`}>Open workspace</a>
              )}
            </div>
          ) : (
            <div className="invitation-action">
              {session.status === "checking" && (
                <div className="invitation-account-check"><span className="progress-orbit" aria-hidden="true" />Checking your Doorway account…</div>
              )}
              {session.status === "sign-in-required" && (
                <>
                  <p>Continue with the invited Google account to join this workspace.</p>
                  <button
                    className="primary-button google-sign-in"
                    type="button"
                    onClick={() => {
                      if (!token) return;
                      setSignInError(null);
                      const returnPath = `/manage/invitations?token=${encodeURIComponent(token)}`;
                      void signIn(returnPath).catch((error: unknown) => {
                        setSignInError(error instanceof Error ? error.message : "Google sign-in could not start.");
                      });
                    }}
                  >
                    <GoogleMark />
                    Continue with Google
                  </button>
                </>
              )}
              {session.status === "ready" && (
                <>
                  <p>Signed in as <strong>{session.user?.email ?? invitation.invitedEmail}</strong></p>
                  <button className="primary-button" type="button" disabled={acceptance.kind === "working"} onClick={join}>
                    {acceptance.kind === "working" ? "Joining…" : `Join ${invitation.workspaceName}`}
                  </button>
                </>
              )}
              {(session.status === "failed" || session.status === "access-required") && (
                <div className="invitation-notice" role="alert">
                  <h2>Doorway could not finish checking your account</h2>
                  <p>Refresh this page to check the same invitation again.</p>
                </div>
              )}
              {session.status === "link-required" && (
                <div className="invitation-notice" role="alert">
                  <h2>Google sign-in is required</h2>
                  <p>Open this invitation on meetdoorway.com and continue with Google.</p>
                </div>
              )}
              {(signInError || actionError) && (
                <div className="invitation-notice" role="alert">
                  <h2>{actionError?.heading ?? "Google sign-in could not start"}</h2>
                  <p>{actionError?.message ?? signInError}</p>
                  {actionError && session.status === "ready" && (
                    <button className="secondary-button" type="button" onClick={join}>Try joining again</button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

function InvitationStatus({
  heading,
  message,
  busy = false,
  workspaceId,
}: {
  heading: string;
  message: string;
  busy?: boolean;
  workspaceId?: string;
}) {
  return (
    <main className="invitation-main">
      <section className="workspace-section invitation-accept">
        <p className="eyebrow">Doorway invitation</p>
        <h1>{heading}</h1>
        <p>{message}</p>
        {busy && <span className="progress-orbit" aria-hidden="true" />}
        {workspaceId && <a className="primary-button" href={`/manage/workspaces/${encodeURIComponent(workspaceId)}`}>Open workspace</a>}
      </section>
    </main>
  );
}

function GoogleMark() {
  return (
    <span className="google-logo" aria-hidden="true">
      <svg viewBox="0 0 18 18" role="img">
        <path fill="#4285F4" d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.909c1.702-1.567 2.683-3.874 2.683-6.614Z" />
        <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.957-2.181l-2.909-2.258c-.806.54-1.835.86-3.048.86-2.344 0-4.328-1.585-5.037-3.714H.956v2.332A9 9 0 0 0 9 18Z" />
        <path fill="#FBBC05" d="M3.963 10.707A5.41 5.41 0 0 1 3.682 9c0-.592.102-1.168.281-1.707V4.961H.956A9 9 0 0 0 0 9c0 1.45.347 2.824.956 4.039l3.007-2.332Z" />
        <path fill="#EA4335" d="M9 3.58c1.321 0 2.507.454 3.441 1.346l2.581-2.581C13.463.892 11.426 0 9 0A9 9 0 0 0 .956 4.961l3.007 2.332C4.672 5.164 6.656 3.58 9 3.58Z" />
      </svg>
    </span>
  );
}

function roleLabel(role: WorkspaceInvitationPreview["role"]): string {
  return role === "creator" ? "Creator" : "Viewer";
}

function terminalInvitationCopy(status: WorkspaceInvitationPreview["status"]): { heading: string; message: string } | null {
  switch (status) {
    case "accepted":
      return { heading: "This invitation was already accepted", message: "Open the workspace to start using Doorway with your team." };
    case "expired":
      return { heading: "This invitation expired", message: "Ask the workspace owner to send a new invitation." };
    case "revoked":
      return { heading: "This invitation was revoked", message: "Ask the workspace owner if you still need access." };
    case "pending":
      return null;
  }
}

function invitationPreviewFailure(cause: unknown): PreviewState {
  if (cause instanceof AccountRequestError && cause.code === "INVITATION_NOT_FOUND") {
    return { kind: "error", heading: "Invitation link invalid", message: "Ask the workspace owner to send the invitation again." };
  }
  return {
    kind: "error",
    heading: "Doorway couldn’t open this invitation",
    message: cause instanceof Error ? cause.message : "Refresh this page to try again.",
  };
}

function invitationFailureState(cause: unknown): AcceptanceState {
  if (cause instanceof AccountRequestError) {
    switch (cause.code) {
      case "INVITATION_EMAIL_MISMATCH":
        return { kind: "error", heading: "Use the invited Google account", message: cause.message };
      case "INVITATION_EXPIRED":
        return { kind: "error", heading: "This invitation expired", message: "Ask the workspace owner to send a new invitation." };
      case "INVITATION_REVOKED":
        return { kind: "error", heading: "This invitation was revoked", message: "Ask the workspace owner if you still need access." };
      case "INVITATION_ALREADY_ACCEPTED":
        return { kind: "error", heading: "Invitation already accepted", message: "You can open Doorway and choose the workspace." };
      case "INVITATION_NOT_FOUND":
        return { kind: "error", heading: "Invitation link invalid", message: "Ask the workspace owner to send the invitation again." };
    }
  }
  return {
    kind: "error",
    heading: "Doorway couldn’t finish joining",
    message: cause instanceof Error ? cause.message : "Try joining again.",
  };
}
